import type { BspReader } from './reader';
import { LUMP } from './reader';

/**
 * The BSP entity lump is plaintext Valve KeyValues: a flat list of
 * `{ "key" "value" ... }` blocks (worldspawn, info_player_*, trigger_*, …). We
 * parse it into plain records; `loadBspLevel` maps the ones we care about to a
 * spawn point and trigger zones. Duplicate keys keep the last value (fine for our
 * use). Robust to stray whitespace and missing braces at EOF.
 */
export type Entity = Record<string, string>;

export function parseEntities(text: string): Entity[] {
  const entities: Entity[] = [];
  let current: Entity | null = null;
  // Tokenizer: quoted strings and the brace characters.
  const re = /"((?:[^"\\]|\\.)*)"|\{|\}/g;
  let pendingKey: string | null = null;
  let match: RegExpExecArray | null;

  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex exec loop
  while ((match = re.exec(text)) !== null) {
    const tok = match[0];
    if (tok === '{') {
      current = {};
      pendingKey = null;
    } else if (tok === '}') {
      if (current) {
        entities.push(current);
      }
      current = null;
      pendingKey = null;
    } else if (current) {
      const value = match[1] ?? '';
      if (pendingKey === null) {
        pendingKey = value;
      } else {
        current[pendingKey] = value;
        pendingKey = null;
      }
    }
  }
  return entities;
}

export function readEntities(r: BspReader): Entity[] {
  return parseEntities(r.readString(LUMP.ENTITIES));
}

/** Parse an "x y z" origin/value into a tuple, or null if malformed. */
export function parseVec3(value: string | undefined): [number, number, number] | null {
  if (!value) return null;
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0] as number, parts[1] as number, parts[2] as number];
}
